import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitEnquiry } from '../lib/enquiries';
import { motion } from 'framer-motion';
import { Reveal, SplitText } from '../components/Reveal';
import { BRAND, HERO_IMAGES, LIFESTYLE, FEATURES, PRODUCTS, SECTORS, PROJECTS_DATA, FAQS } from '../data/mock';
import { ArrowUpRight, Leaf, Shield, Wrench, Droplet, ArrowRight, Award, Phone, Calculator, Check } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';
import HeroCarousel from '../components/HeroCarousel';
import NumberedMarquee from '../components/NumberedMarquee';
import GuaranteesStrip from '../components/GuaranteesStrip';
import GroundworksUSP from '../components/GroundworksUSP';
import ProjectsShowcase from '../components/ProjectsShowcase';
import ReviewsShowcase from '../components/ReviewsShowcase';
import AnimatedSection from '../components/AnimatedSection';
import HomeProcess from '../components/HomeProcess';
import FAQ from '../components/FAQ';
import useSeo from '../hooks/useSeo';

const ICON_MAP = { leaf: Leaf, shield: Shield, wrench: Wrench, droplet: Droplet };

export default function Home() {
  useSeo({
    title: null, // use default site title on home (cleanest for SERPs)
    description:
      'Premium artificial lawn installation across London, Kent, Surrey & Essex. Civil-engineered foundations, 10-year guarantee. Free site survey & 48-hour quote.',
    url: 'https://guardianartificiallawns.co.uk/',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': 'https://guardianartificiallawns.co.uk/#organization',
        name: 'Guardian Artificial Lawns',
        alternateName: 'Guardian Artificial Lawns by Guardian Builders Ltd',
        url: 'https://guardianartificiallawns.co.uk/',
        logo: {
          '@type': 'ImageObject',
          url: 'https://guardianartificiallawns.co.uk/guardian-logo.png',
          width: 1400,
          height: 402,
        },
        foundingDate: '2004',
        slogan: 'Built from the ground up.',
        sameAs: [
          'https://www.instagram.com/guardianartificiallawns/',
          'https://www.guardianartificiallawns.co.uk/',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+441634557852',
          contactType: 'customer service',
          email: 'info@guardianartificiallawns.co.uk',
          areaServed: 'GB',
          availableLanguage: ['en-GB'],
        },
        parentOrganization: { '@type': 'Organization', name: 'Guardian Builders Ltd' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': 'https://guardianartificiallawns.co.uk/#website',
        name: 'Guardian Artificial Lawns',
        url: 'https://guardianartificiallawns.co.uk/',
        inLanguage: 'en-GB',
        publisher: { '@id': 'https://guardianartificiallawns.co.uk/#organization' },
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://guardianartificiallawns.co.uk/projects?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': 'https://guardianartificiallawns.co.uk/#faq',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });
  return (
    <main className="relative bg-[#314F2B] text-[#E7E9E4]">
      {/* HERO — FULL-BLEED CAROUSEL + ESTIMATE BOX */}
      <section className="relative min-h-screen pt-32 md:pt-36 overflow-hidden">
        <div className="absolute inset-0">
          <HeroCarousel
            slides={[
              { src: '/hero/hero-patio.jpg',      webp: '/hero/hero-patio.webp',      alt: 'Premium artificial lawn — modern home with patio & garden' },
              { src: '/hero/hero-hand-grass.jpg', webp: '/hero/hero-hand-grass.webp', alt: 'Hand running through soft artificial grass blades' },
              { src: '/hero/hero-stone-path.jpg', webp: '/hero/hero-stone-path.webp', alt: 'Manicured back garden with stone-stepped path through Guardian lawn' },
            ]}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#162A1E]/85 via-[#162A1E]/45 to-[#162A1E]/10" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#314F2B] to-transparent" />
        </div>

        <div className="relative z-10 mx-auto px-6 md:px-12 w-full" style={{ maxWidth: '1700px' }}>
          <div className="grid grid-cols-12 gap-8 items-center min-h-[calc(100vh-9rem)] py-12">

            {/* LEFT: Text + CTAs */}
            <div className="col-span-12 lg:col-span-7 xl:col-span-7">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-8 max-w-full">
                <span className="w-2 h-2 rounded-full bg-[#A3C66F] shrink-0" />
                <span className="text-[10px] tracking-[0.22em] sm:tracking-[0.4em] uppercase text-[#E7E9E4] font-bold" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>Transforming Gardens · Enriching Lives</span>
                <span className="hidden sm:block w-12 h-px bg-[#A3C66F]/50 shrink-0" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#E7E9E4] font-semibold">Est. 2004</span>
              </div>

              <h1 className="font-sans-brand uppercase text-white font-extrabold leading-[0.95] tracking-[-0.01em]" style={{ fontSize: 'clamp(2.2rem, 6.2vw, 7rem)' }}>
                <span className="block">Beautiful</span>
                <span className="block">Artificial Lawns.</span>
                <span className="block">Our Expertise.</span>
              </h1>

              <p className="mt-8 text-base md:text-lg text-[#E7E9E4]/85 leading-relaxed max-w-[560px]">
                At Guardian, we transform outdoor spaces with premium artificial lawns that look beautiful, feel natural and last for years.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link to="/contact" data-cursor="hover" data-cursor-label="Begin">
                  <MagneticButton className="group inline-flex items-center gap-3 bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] pl-7 pr-3 py-3 rounded-full transition-colors duration-500" as="div">
                    <span className="text-[11px] tracking-[0.3em] uppercase font-extrabold">Get In Touch</span>
                    <span className="w-10 h-10 rounded-full bg-[#162A1E] flex items-center justify-center">
                      <ArrowRight size={14} className="text-[#6A8F3E]" strokeWidth={2.4} />
                    </span>
                  </MagneticButton>
                </Link>
                <a href={`tel:${BRAND.phoneTel}`} data-cursor="hover" data-cursor-label="Call" className="inline-flex items-center gap-3 text-[#E7E9E4]/85 hover:text-[#6A8F3E] transition-colors group">
                  <span className="w-11 h-11 rounded-full border border-[#6A8F3E]/40 group-hover:border-[#6A8F3E] group-hover:bg-[#6A8F3E]/10 flex items-center justify-center transition-all">
                    <Phone size={14} strokeWidth={1.6} />
                  </span>
                  <div>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-[#E7E9E4]/75 font-semibold">Call our team</div>
                    <div className="text-sm font-semibold">{BRAND.phone}</div>
                  </div>
                </a>
              </div>
            </div>

            {/* RIGHT: Estimate Quote Card — glass on hero */}
            <div className="col-span-12 lg:col-span-5 xl:col-span-4 lg:col-start-9 relative">
              <EstimateCard />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 text-[#E7E9E4]/50">
          <span className="text-[9px] tracking-[0.4em] uppercase">Scroll to Discover</span>
          <div className="relative w-px h-10 bg-[#E7E9E4]/10 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-5 bg-gradient-to-b from-[#6A8F3E] to-transparent scroll-dot" />
          </div>
        </div>
      </section>

      {/* GUARANTEES MARQUEE — animated horizontal banner sits directly under the hero so trust signals are the first thing visitors see after the headline + estimate card. */}
      <GuaranteesStrip compact />

      {/* LATEST WORKS — cinematic full-bleed projects carousel.
          Positioned high on the page so the FIRST thing a visitor sees
          after the hero is real, photographic proof of finished gardens.
          Strongest social-proof signal we have — beats any editorial copy. */}
      <ProjectsShowcase projects={PROJECTS_DATA} />

      {/* CLIENT REVIEWS — hidden for now (pending Google Place ID + verified testimonials) */}
      {/* <ReviewsShowcase /> */}

      {/* ============ BENEFITS — "Built around four simple promises".
          Sits at #3 (right after the social-proof carousel) because once
          a visitor has seen real installs, the question they ask next is
          "what am I actually getting?" — and these 4 promise tiles answer
          it crisply: Natural Look, Built To Last, Low Maintenance, Sustainable.
          ============ */}
      <AnimatedSection
        data-testid="home-benefits"
        className="relative py-20 md:py-28 px-6 md:px-12 bg-[#E7E9E4] text-[#1A1A1A] overflow-hidden border-b border-[#1A1A1A]/8"
      >
        <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-[#6A8F3E]/6 blur-[140px]" />

        <div className="mx-auto relative" style={{ maxWidth: '1500px' }}>
          <div className="text-center mb-14 md:mb-16 max-w-2xl mx-auto">
            <Reveal>
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="w-10 h-px bg-[#6A8F3E]" />
                <span className="text-[10px] tracking-[0.4em] uppercase text-[#314F2B] font-bold">Why Guardian</span>
                <span className="w-10 h-px bg-[#6A8F3E]" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-sans-brand uppercase font-extrabold tracking-tight leading-[0.98]" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.6rem)' }}>
                Built around four
                <span className="block font-serif-editorial italic font-light text-[#6A8F3E] mt-2 normal-case">simple promises.</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { Icon: Leaf,      title: 'Natural Look',       body: 'Hand-tufted blades with natural variance — looks indistinguishable from real lawn.' },
              { Icon: Shield,    title: 'Built To Last',      body: '10-year product guarantee and 24-month groundworks guarantee on every install.' },
              { Icon: Droplet,   title: 'Low Maintenance',    body: 'No watering, no mowing, no fertiliser — a few minutes a month and your lawn looks new.' },
              { Icon: Award,     title: 'Sustainable Choice', body: 'Fully recyclable products, SUDS-compliant drainage, no chemicals into the garden.' },
            ].map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className="bg-white rounded-xl border border-[#1A1A1A]/8 p-6 md:p-7 hover:border-[#6A8F3E]/40 hover:-translate-y-1 transition-all duration-500"
                data-testid={`benefit-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#6A8F3E]/12 border border-[#6A8F3E]/30 flex items-center justify-center mb-5">
                  <Icon size={20} className="text-[#314F2B]" strokeWidth={1.8} />
                </div>
                <h3 className="font-sans-brand uppercase font-bold text-base md:text-lg leading-tight mb-2 text-[#1A1A1A]">{title}</h3>
                <p className="text-sm text-[#1A1A1A]/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* IMMERSIVE PARALLAX STRIP — full-bleed lifestyle photo of a lush
          Guardian-style lawn beside rattan garden furniture. */}
      <ParallaxStrip
        image="/hero/hero-stone-path.jpg"
        eyebrow="A home, fully lived in"
        quote="Lawns that feel like part of the living room."
      />

      {/* OUR PROCESS — extracted into a self-contained component that
          manages its own `visited` state via localStorage. */}
      <HomeProcess />

      {/* GROUNDWORKS USP — cinematic boots-on-pavers texture backdrop.
          Sits AFTER the process section so visitors have already walked through
          the simple steps, then the deeper technical "why we're different"
          story lands as a closing trust argument. */}
      <GroundworksUSP />

      {/* OUR COMMITMENT TO EXCELLENCE - 6-card numbered grid (matches template) */}
      <AnimatedSection className="relative bg-[#E7E9E4] text-[#1A1A1A] py-16 md:py-32 px-6 md:px-12 why-guardian-bg">
        <div className="mx-auto" style={{ maxWidth: '1700px' }}>
          <Reveal>
            <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
              <div>
                <div className="eyebrow mb-6" style={{ color: '#314F2B' }}>Why Guardian</div>
                <h2 className="font-serif-editorial text-4xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-[#1A1A1A]">
                  Our commitment to <span className="italic font-light text-[#6A8F3E]">excellence.</span>
                </h2>
              </div>
              <Link to="/about" data-cursor="hover" data-cursor-label="More" className="text-[11px] tracking-[0.3em] uppercase link-underline font-semibold text-[#1A1A1A]">Discover the difference</Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 md:gap-y-16">
            {FEATURES.map((f, i) => {
              const Icon = ICON_MAP[f.icon];
              return (
                <Reveal key={f.t} delay={i * 0.08} className="relative">
                  {/* Big template-style numbered badge — info card, no false interactivity */}
                  <div className="flex items-start gap-5 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full border border-[#314F2B]/30 flex items-center justify-center text-[#314F2B]">
                        <Icon size={24} strokeWidth={1.4} />
                      </div>
                      <div className="text-[10px] tracking-[0.35em] uppercase text-[#6A8F3E] font-bold text-center mt-3">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-serif-editorial text-2xl md:text-3xl leading-tight mb-3 text-[#1A1A1A]">{f.title}</h3>
                      <p className="text-sm md:text-base text-[#1A1A1A]/70 leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* PROCESS LAYERS section REMOVED — was a duplicate of the engineered cross-section
          already shown inside the GroundworksUSP component at the top of the page. */}

      {/* NUMBERED SERVICES MARQUEE — Guardian's 6 lawn types */}
      <NumberedMarquee items={['Supreme Lawn', 'Prestige Lawn', 'Comfort Lawn', 'Pet Lawn', 'Active Lawn', 'Natural Lawn']} />

      {/* FEATURED PRODUCTS — editorial flagship + side-rail showcase.
          Now sits on a lighter cream stage so the dark grass-photo product
          cards pop with maximum contrast (was a heavy forest-green wall). */}
      <AnimatedSection className="relative py-16 md:py-40 px-6 md:px-12 bg-[#E7E9E4] border-y border-[#1A1A1A]/8 overflow-hidden">
        {/* Soft sage atmosphere — breaks up the bright cream */}
        <div className="absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/10 blur-[160px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#314F2B]/8 blur-[140px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #314F2B 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }} />

        <div className="relative mx-auto" style={{ maxWidth: '1700px' }}>
          <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-6">
            <Reveal>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#314F2B] font-bold mb-6">The Range — Six Lawns</div>
                <h2 className="font-serif-editorial text-4xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-[#1A1A1A]">
                  Hand-picked, <span className="italic font-light text-[#6A8F3E]">European</span> made.
                </h2>
                <p className="text-sm md:text-base text-[#1A1A1A]/70 leading-relaxed mt-5 max-w-xl">
                  Each lawn in our range is hand-selected, UV-stabilised and engineered for the British climate — from luxurious 45mm flagship turf to engineered pet-safe specifications.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/products" data-cursor="hover" data-cursor-label="All" className="text-[11px] tracking-[0.3em] uppercase text-[#314F2B] hover:text-[#6A8F3E] link-underline font-semibold inline-flex items-center gap-2">
                View all products
                <ArrowUpRight size={14} strokeWidth={2.2} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {PRODUCTS.slice(0, 6).map((p) => (
              <div key={p.slug}>
                <Link
                  to="/products"
                  data-cursor="hover"
                  data-cursor-label="View"
                  data-testid={`home-product-card-${p.slug}`}
                  className="group block relative aspect-[4/5] sm:aspect-[4/5] rounded-md overflow-hidden bg-[#1A1A1A]"
                >
                  <img
                    src={p.image}
                    alt={`${p.name} ${p.pile}`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                    style={{ objectPosition: '50% 40%', transform: 'scale(1.4)' }}
                  />
                  {/* Subtle bottom shadow only — keeps image clean and bright */}
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#162A1E]/55 to-transparent pointer-events-none" />

                  {/* Pile-size badge — refined Gardyn-style: thin outline, cream text, no fill */}
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E7E9E4]/45 bg-[#162A1E]/25 backdrop-blur-md text-[#E7E9E4] text-[10px] tracking-[0.3em] uppercase font-semibold">
                      <span className="w-1 h-1 rounded-full bg-[#6A8F3E]" />
                      {p.pile}
                    </span>
                  </div>

                  {/* Open indicator — refined Gardyn ring */}
                  <span className="absolute top-4 right-4 w-10 h-10 rounded-full border border-[#E7E9E4]/45 bg-[#162A1E]/25 backdrop-blur-md group-hover:bg-[#6A8F3E] group-hover:border-[#6A8F3E] flex items-center justify-center transition-all duration-500">
                    <ArrowUpRight size={14} className="text-[#E7E9E4] group-hover:text-[#162A1E] group-hover:rotate-45 transition-all" strokeWidth={2.2} />
                  </span>

                  {/* FROSTED INFO PANEL — always visible at bottom (no image darken/blur behind) */}
                  <div
                    className="absolute inset-x-3 bottom-3 rounded-xl p-4 md:p-5"
                    style={{
                      backdropFilter: 'blur(18px) saturate(140%)',
                      WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                      background: 'linear-gradient(135deg, rgba(231,233,228,0.14), rgba(22,42,30,0.42))',
                      border: '1px solid rgba(231,233,228,0.22)',
                      boxShadow: '0 20px 50px -20px rgba(22,42,30,0.55), inset 0 1px 0 rgba(231,233,228,0.18)',
                    }}
                  >
                    <h3 className="font-sans-brand uppercase font-bold text-lg md:text-xl leading-tight text-[#E7E9E4] tracking-tight">{p.name}</h3>
                    <p className="text-[12px] text-[#E7E9E4]/80 leading-snug mt-1.5 mb-3 line-clamp-2">{p.tagline}</p>
                    <ul className="space-y-1 pt-3 border-t border-[#E7E9E4]/20">
                      {p.tags.slice(0, 3).map((t) => (
                        <li key={t} className="flex items-center gap-2 text-[11px] text-[#E7E9E4]/90">
                          <Check size={12} className="text-[#6A8F3E] flex-shrink-0" strokeWidth={2.6} />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* SECTORS — 4 categories (residential/sports/education/commercial) — frosted glass */}
      <AnimatedSection className="relative py-16 md:py-40 px-6 md:px-12 overflow-hidden bg-[#314F2B] border-y border-[#E7E9E4]/8">
        {/* Ambient glows + grain to give the dark green section depth */}
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/12 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#314F2B]/30 blur-[140px] pointer-events-none" />
        <div className="absolute inset-0 grain opacity-25 pointer-events-none" />

        <div className="mx-auto relative" style={{ maxWidth: '1700px' }}>
          <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-6">
              <Reveal><div className="eyebrow mb-7">Where we work</div></Reveal>
              <h2 className="font-serif-editorial text-4xl md:text-7xl leading-[0.98] tracking-tight text-[#E7E9E4]">
                <SplitText text="Four sectors." />
                <span className="block italic font-light text-[#6A8F3E] mt-3"><SplitText text="One standard." delay={0.18} /></span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col justify-end">
              <Reveal delay={0.2}>
                <p className="text-base md:text-lg text-[#E7E9E4]/75 leading-relaxed">
                  From private gardens to commercial show-homes, sports surfaces and education spaces — every Guardian installation is delivered to the same exacting brief.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {SECTORS.map((s, i) => (
              <div
                key={s.slug}
                className="group relative rounded-2xl overflow-hidden lift-on-hover"
                data-cursor="hover"
                data-cursor-label="Explore"
              >
                <Link to="/contact" className="block">
                  {/* Embedded photo — sector lawn imagery */}
                  <div className="relative aspect-[5/4] overflow-hidden">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={s.image}
                      alt={s.alt || s.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.06]"
                      style={{ objectPosition: s.objectPosition || 'center 60%' }}
                    />
                    {/* Subtle dark gradient at the bottom to ease photo into the frosted body */}
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-[#162A1E]/65" />
                    {/* Sector number badge */}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#162A1E]/55 backdrop-blur-md text-[9px] tracking-[0.35em] uppercase text-[#E7E9E4] font-bold border border-[#E7E9E4]/15">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    {/* Arrow corner */}
                    <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-[#162A1E]/55 backdrop-blur-md border border-[#E7E9E4]/15 group-hover:bg-[#6A8F3E] group-hover:border-[#6A8F3E] flex items-center justify-center transition-all duration-500">
                      <ArrowUpRight size={14} className="text-[#E7E9E4] group-hover:text-[#162A1E] group-hover:rotate-45 transition-all" />
                    </div>
                  </div>

                  {/* Frosted info panel — Gardyn-style heavy blur over a glassy gradient,
                      with a hairline accent at the top edge and subtle inner highlight.
                      Replaces the previous flat green body. */}
                  <div
                    className="relative p-5 md:p-6"
                    style={{
                      backdropFilter: 'blur(24px) saturate(140%)',
                      WebkitBackdropFilter: 'blur(24px) saturate(140%)',
                      background: 'linear-gradient(135deg, rgba(231,233,228,0.16) 0%, rgba(106,143,62,0.10) 50%, rgba(22,42,30,0.55) 100%)',
                      boxShadow: 'inset 0 1px 0 rgba(231,233,228,0.22), inset 0 -1px 0 rgba(0,0,0,0.15)',
                    }}
                  >
                    <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#6A8F3E]/45 to-transparent pointer-events-none" />
                    <h3 className="font-serif-editorial text-2xl md:text-3xl leading-tight mb-3 text-[#E7E9E4]" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>{s.title}</h3>
                    <p className="text-sm text-[#E7E9E4]/85 leading-relaxed mb-4 line-clamp-3">{s.body}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.examples.map((e) => (
                        <span
                          key={e}
                          className="text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full text-[#E7E9E4] font-bold bg-[#6A8F3E]/22 border border-[#6A8F3E]/45 backdrop-blur-md"
                        >
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* FAQ — common questions before they call */}
      <FAQ limit={6} light />
    </main>
  );
}

// ===== Sub-components =====

// Estimate Card — single-screen lead capture (Name / Address / Phone / Email)
// Per client brief: just collect details, no project type / size step.
function EstimateCard() {
  const [data, setData] = useState({ name: '', address: '', phone: '', email: '' });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr('');
    setSubmitting(true);
    try {
      await submitEnquiry({
        name: data.name.trim() || 'Hero Estimate',
        email: data.email.trim(),
        phone: data.phone.trim(),
        address: data.address.trim() || null,
        project_type: 'residential',
        source: 'estimate-box',
      });
      setDone(true);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(' · ') : 'Could not send. Please call us.';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="glass rounded-3xl p-8 md:p-9 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#6A8F3E]/20 blur-3xl" />
        <div className="relative text-center py-4">
          <div className="w-14 h-14 rounded-full bg-[#6A8F3E]/20 border border-[#6A8F3E] flex items-center justify-center mx-auto mb-5">
            <Check size={24} className="text-[#6A8F3E]" strokeWidth={2} />
          </div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#6A8F3E] font-semibold mb-3">Thank you</div>
          <h4 className="font-serif-editorial text-2xl md:text-3xl mb-3 leading-tight">Your enquiry has been received.</h4>
          <p className="text-sm text-[#E7E9E4]/65 leading-relaxed mb-6">A member of our team will contact you shortly.</p>
          <button onClick={() => { setDone(false); setData({ name: '', address: '', phone: '', email: '' }); }} data-cursor="hover" className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] link-underline font-semibold">
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-3xl p-7 md:p-8 relative overflow-hidden" data-testid="estimate-card">
      <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full bg-[#6A8F3E]/15 blur-3xl" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-5">
          <Calculator size={14} className="text-[#A3C66F]" strokeWidth={2.2} />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#A3C66F] font-bold">Free Estimate</span>
        </div>

        <h3 className="font-serif-editorial text-2xl md:text-[28px] leading-[1.2] tracking-tight mb-1 text-[#E7E9E4]">
          Get a no-obligation quote.
        </h3>
        <p className="text-[12px] text-[#E7E9E4]/80 mb-6 leading-relaxed">A specialist will follow up within 24 hours.</p>

        <form onSubmit={submit}>
          <div className="space-y-4 mb-5">
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#A3C66F] font-bold block mb-1.5">Your name</label>
              <input
                type="text"
                required
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
                className="w-full bg-[#0F1A14]/55 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/75 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/55 transition-all"
                placeholder="Jane Smith"
                data-testid="estimate-name"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#A3C66F] font-bold block mb-1.5">Address</label>
              <input
                type="text"
                required
                value={data.address}
                onChange={(e) => setData({ ...data, address: e.target.value })}
                className="w-full bg-[#0F1A14]/55 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/75 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/55 transition-all"
                placeholder="Street, town, postcode"
                data-testid="estimate-address"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#A3C66F] font-bold block mb-1.5">Phone number</label>
              <input
                type="tel"
                required
                value={data.phone}
                onChange={(e) => setData({ ...data, phone: e.target.value })}
                className="w-full bg-[#0F1A14]/55 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/75 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/55 transition-all"
                placeholder="07900 000 000"
                data-testid="estimate-phone"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#A3C66F] font-bold block mb-1.5">Email</label>
              <input
                type="email"
                required
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                className="w-full bg-[#0F1A14]/55 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/75 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/55 transition-all"
                placeholder="jane@example.com"
                data-testid="estimate-email"
              />
            </div>
          </div>
          {err && (
            <div data-testid="estimate-form-error" className="text-[11px] text-red-200 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2 mb-3">
              {err}
            </div>
          )}
          <button type="submit" disabled={submitting} data-cursor="hover" data-cursor-label="Send" data-testid="estimate-submit" className="group w-full inline-flex items-center justify-center gap-3 bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] px-6 py-3.5 rounded-full transition-all font-extrabold text-[11px] tracking-[0.3em] uppercase disabled:opacity-60 shadow-[0_8px_24px_rgba(106,143,62,0.4)]">
            {submitting ? 'Sending…' : <>Send Enquiry <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" strokeWidth={2.6} /></>}
          </button>
          <div className="text-[11px] text-[#E7E9E4]/75 leading-relaxed mt-4 text-center">
            We typically respond within 24 hours. No spam, ever.
          </div>
        </form>
      </div>
    </div>
  );
}

// Large 3D-feel spiral logo for hero
function SpiralLogoBig() {
  return (
    <motion.svg
      width="240"
      height="240"
      viewBox="0 0 240 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-[0_30px_80px_rgba(106,143,62,0.5)]"
      initial={{ rotate: -8, opacity: 0 }}
      animate={{ rotate: 0, opacity: 1 }}
      transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <defs>
        <linearGradient id="bigGrassGrad" x1="0" y1="0" x2="240" y2="240" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#C5E29A" />
          <stop offset="0.45" stopColor="#6A8F3E" />
          <stop offset="0.8" stopColor="#6A8F3E" />
          <stop offset="1" stopColor="#314F2B" />
        </linearGradient>
        <filter id="bigGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <motion.path
        d="M 120 120 c 0 -10 8 -18 18 -18 c 14 0 24 11 24 26 c 0 19 -16 32 -36 32 c -25 0 -46 -22 -46 -50 c 0 -34 28 -62 64 -62 c 42 0 76 35 76 78"
        stroke="url(#bigGrassGrad)"
        strokeWidth="16"
        strokeLinecap="round"
        fill="none"
        filter="url(#bigGlow)"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.line
        x1="35" y1="218" x2="220" y2="218"
        stroke="#6A8F3E" strokeWidth="4" strokeLinecap="round" opacity="0.4"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 1.0 }}
      />
      <motion.line
        x1="35" y1="218" x2="150" y2="218"
        stroke="#6A8F3E" strokeWidth="5" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 1.2 }}
      />
      <motion.ellipse
        cx="120" cy="225" rx="100" ry="6" fill="#6A8F3E"
        initial={{ opacity: 0 }} animate={{ opacity: 0.3 }}
        transition={{ duration: 1, delay: 1.4 }}
        filter="url(#bigGlow)"
      />
    </motion.svg>
  );
}


/* =============================================================
   ParallaxStrip — full-bleed lifestyle photo break-strip. The
   image translates slowly upward as you scroll past for a soft
   editorial parallax. Used between the EditorialIntro and the
   light-cream Process section on Home.
   ============================================================= */
function ParallaxStrip({ image, eyebrow, quote }) {
  return (
    <section
      data-testid="lifestyle-strip"
      className="relative overflow-hidden h-[60vh] md:h-[78vh] border-y border-[#E7E9E4]/8"
    >
      <img
        src={image}
        alt="A premium lawn beside rattan garden furniture"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 60%' }}
        loading="lazy"
        decoding="async"
      />
      {/* Top hairline shadow — visually separates from the dark forest USP section above */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#162A1E] via-[#162A1E]/40 to-transparent pointer-events-none" />
      {/* Left readable column — wide, strong gradient so the pull-quote pops cleanly. */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#162A1E]/95 via-[#162A1E]/75 to-[#162A1E]/0" style={{ backgroundImage: 'linear-gradient(to right, rgba(22,42,30,0.95) 0%, rgba(22,42,30,0.82) 30%, rgba(22,42,30,0.45) 55%, rgba(22,42,30,0) 75%)' }} />
      <div className="absolute inset-0 grain opacity-30" />

      <div className="relative mx-auto h-full px-6 md:px-12 flex items-center" style={{ maxWidth: '1700px' }}>
        <div className="max-w-[680px]">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#E7E9E4]/95 mb-5 font-semibold inline-flex items-center gap-2">
              <span className="w-8 h-px bg-[#6A8F3E]" />
              {eyebrow}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-serif-editorial text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E7E9E4]" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.55)' }}>
              {quote}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
